import React from "react";
import NewAppointmentProps from "../interface/newAppointmentProps";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { AppContext, User } from "../../../../context/appContext";
import { endpoints } from "../../../../endpoints/endpoints";
import { DoCreateAppointmentPayloadProps } from "../interface/doCreateAppointmentPayloadProps";

export const NewAppointmentContext = React.createContext<any>({});

const NewAppointmentProvider = ({ children }: any): React.JSX.Element => {
  const {
    user,
    checkSession,
  }: {
    user: User;
    checkSession: (response: any) => void;
  } = React.useContext(AppContext);

  const [newAppointment, setNewAppointment] =
    React.useState<NewAppointmentProps>({
      type: null,
      date: new Date().toISOString().split("T")[0],
      time: { from: "", to: "" },
      directBooking: false,
      coach: null,
      search: {
        loading: false,
        participantName: "",
        searchResults: [],
        selectedParticipant: null,
      },
      creationIsLoading: false,
    });

  const doCreateAppointment = React.useCallback(async (): Promise<void> => {
    setNewAppointment({ ...newAppointment, creationIsLoading: true });

    const payload: DoCreateAppointmentPayloadProps = {
      sessionId: user.sessionId,
      type: newAppointment.type?.label,
      date: newAppointment.date,
      fromTime: newAppointment.time.from,
      tillTime: newAppointment.time.to,
      coachId: newAppointment.coach?.value,
      participantId: newAppointment.directBooking
        ? newAppointment.search.selectedParticipant?.value
        : null,
    };

    await axios
      .post(endpoints.admin.createAppointment, payload)
      .then((response) => {
        checkSession(response);

        setNewAppointment({ ...newAppointment, creationIsLoading: false });
        enqueueSnackbar(
          response.data.meta.status === "success"
            ? "Der Termin wurde erfolgreich erstellt!"
            : "Der Termin konnte nicht erstellt werden!",
          {
            variant: response.data.meta.status,
          }
        );
      })
      .catch((error) => {
        console.log(error);
        setNewAppointment({ ...newAppointment, creationIsLoading: false });
        enqueueSnackbar("Es ist ein unerwarteter Fehler aufgetreten!", {
          variant: "error",
        });
      });
  }, [checkSession, newAppointment, user.sessionId]);

  const startSearch = React.useCallback(async (): Promise<void> => {
    if (newAppointment.search.loading) return;

    setNewAppointment({
      ...newAppointment,
      search: {
        ...newAppointment.search,
        loading: true,
      },
    });

    await axios
      .post(endpoints.admin.searchParticipant, {
        sessionId: user.sessionId,
        searchString: newAppointment.search.participantName,
      })
      .then((response) => {
        checkSession(response);

        const sortetUser = response.data.data.map((user: any) => {
          return {
            label: `${user.firstName} ${user.lastName} (${user.email})`,
            value: user.participantId,
          };
        });

        setNewAppointment({
          ...newAppointment,
          search: {
            ...newAppointment.search,
            loading: false,
            searchResults: sortetUser,
          },
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }, [checkSession, newAppointment, user.sessionId]);

  const value: {
    newAppointment: NewAppointmentProps;
    setNewAppointment: React.Dispatch<
      React.SetStateAction<NewAppointmentProps>
    >;
    doCreateAppointment: () => Promise<void>;
    startSearch: () => Promise<void>;
  } = React.useMemo(
    () => ({
      newAppointment,
      setNewAppointment,
      doCreateAppointment,
      startSearch,
    }),
    [doCreateAppointment, newAppointment, startSearch]
  );

  return (
    <NewAppointmentContext.Provider value={value}>
      {children}
    </NewAppointmentContext.Provider>
  );
};

export default NewAppointmentProvider;
